import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { usePageMeta } from "../hooks/usePageMeta";

export default function TermsPage() {
  usePageMeta("terms");
  
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-20 md:pt-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl uppercase mb-8">
            Terms of Service
          </h1>
          
          <div className="prose prose-zinc max-w-none font-body">
            <p className="text-zinc-600 mb-6">
              Last updated: September 2026
            </p>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing or using the Blackline Estimating website and services, including the Tender Red Review service, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">2. Service Description</h2>
              <p>
                Blackline Estimating provides independent cost engineering services including pre-submission tender review and estimating for construction projects in New Zealand and Australia.
              </p>
              
              {/* Boundary Statement */}
              <div className="my-6 p-4 bg-zinc-100 border-l-4 border-black">
                <p className="font-body text-zinc-800">
                  <strong>Important:</strong> Blackline provides estimating and cost engineering services. Our services do not replace project-specific professional advice from qualified engineers, architects, quantity surveyors, or legal professionals. Clients remain responsible for all final decisions regarding tender submissions and project execution.
                </p>
              </div>

              <p className="mt-4">Our Tender Red Review service is an independent commercial review, not:</p>
              <ul className="list-disc pl-6">
                <li>Legal advice</li>
                <li>Engineering advice</li>
                <li>A quantity surveyor's report or professional certification</li>
                <li>A complete independent remeasurement</li>
                <li>A guarantee of tender success or project profitability</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">3. Customer Responsibilities</h2>
              <p>You are responsible for:</p>
              <ul className="list-disc pl-6">
                <li>Providing accurate and complete information</li>
                <li>Ensuring you have the right to share submitted documents</li>
                <li>Making final tender and submission decisions</li>
                <li>Verifying review findings before acting on them</li>
                <li>Paying agreed fees for services rendered</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">4. Fees and Payment</h2>
              <p>
                Service fees are quoted and confirmed before work commences. The founding Tender Red Review fee is NZ$595 (New Zealand) or A$545 (Australia) excluding GST for substantially complete standard reviews. Larger, incomplete, or complex packages are quoted separately. All advertised prices exclude GST.
              </p>
              <p className="mt-4">
                <strong>Payment timing:</strong> Payment is due after Blackline accepts the submission and before review work begins. Scope, fee, and turnaround will be confirmed before payment is requested.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">5. Cancellation and Refunds</h2>
              <p>
                <strong>Before work begins:</strong> If you cancel your submission before review work commences, you will receive a full refund of any payment made.
              </p>
              <p className="mt-4">
                <strong>After work begins:</strong> If you cancel after review work has commenced, Blackline may charge for work completed up to the point of cancellation and refund any remaining balance. The amount charged will be calculated on a pro-rata basis or by reasonable assessment of work completed.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">6. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, Blackline Estimating's liability for any claims arising from or related to our services is limited to the fees paid for the specific service giving rise to the claim. We are not liable for:
              </p>
              <ul className="list-disc pl-6">
                <li>Tender outcomes or project profitability</li>
                <li>Errors in customer-supplied documents</li>
                <li>Decisions made based on our review findings</li>
                <li>Indirect, consequential, or incidental damages</li>
              </ul>
              <p className="mt-4">
                Nothing in these Terms excludes, restricts or modifies any right, guarantee or remedy that cannot lawfully be excluded under applicable New Zealand or Australian consumer legislation.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">7. Intellectual Property</h2>
              <p>
                You retain all rights to your submitted documents. We retain rights to our review methodologies, tools, and general knowledge. We may use anonymised, aggregated insights to improve our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">8. Confidentiality</h2>
              <p>
                We treat all submitted documents and tender information as strictly confidential. See our{" "}
                <Link to="/confidentiality" className="underline">Confidentiality and Document Handling</Link>{" "}
                page for details.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">9. Governing Law</h2>
              <p>
                These terms are governed by the laws of New Zealand. Any disputes will be resolved in the courts of New Zealand.
              </p>
              <p className="mt-4">
                Where services are provided to customers in Australia, the choice of New Zealand governing law does not exclude or limit any mandatory consumer rights or guarantees that apply under Australian law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">10. Contact</h2>
              <p>
                For questions about these terms, contact us at:{" "}
                <a href="mailto:info@blacklineestimating.com" className="underline">
                  info@blacklineestimating.com
                </a>
              </p>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-zinc-200">
            <Link to="/" className="font-mono text-sm uppercase tracking-wider hover:underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
